import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  TablePagination,
  styled,
  tableCellClasses,
  Chip,
  TextField,
  Stack,
  Button,
  Box,
} from "@mui/material";
import TextInput from "../InputComponents/TextInput";
import { Search, RestartAlt } from "@mui/icons-material";
import CommonButton from "../InputComponents/CommonButton";

// Styled table components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const AuditLog = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [AuditLogData, setAuditLogData] = useState([
    {
      timestamp: "2021-03-25T09:00:00",
      admin: "admin",
      action: "added candidate",
      module: "Candidates",
      details: "Added Dr. Anil Kumar",
      ip_address: "192.168.0.10",
      affectedUser: "Dr. Anil Kumar",
      status: "Success",
    },
    {
      timestamp: "2021-03-25T10:30:00",
      admin: "admin",
      action: "edited candidate",
      module: "Candidates",
      details: "Updated profile of Dr. Fatima Rahman",
      ip_address: "192.168.0.11",
      affectedUser: "Dr. Fatima Rahman",
      status: "Success",
    },
    {
      timestamp: "2021-03-25T12:00:00",
      admin: "admin",
      action: "added client",
      module: "Clients",
      details: "Added Al Zahra Hospital, UAE",
      ip_address: "192.168.0.12",
      affectedUser: "Al Zahra Hospital",
      status: "Success",
    },
    {
      timestamp: "2021-03-25T14:00:00",
      admin: "admin",
      action: "added job",
      module: "Jobs",
      details: "Posted job for Staff Nurse - Oman",
      ip_address: "192.168.0.13",
      affectedUser: "Oman Jobs",
      status: "Success",
    },
    {
      timestamp: "2021-03-25T15:30:00",
      admin: "admin",
      action: "approved application",
      module: "Applications",
      details: "Approved application of Dr. John Mathew",
      ip_address: "192.168.0.14",
      affectedUser: "Dr. John Mathew",
      status: "Success",
    },
  ]);

  const [searchAdmin, setSearchAdmin] = useState("");
  const [searchAffectedUser, setSearchAffectedUser] = useState("");
  const [searchAction, setSearchAction] = useState("");
  const [filterDate, setFilterDate] = useState("");

  const [filteredData, setFilteredData] = useState(AuditLogData);

  const handleDateChange = (e) => {
    setFilterDate(e.target.value);
  };

  const handleSearch = () => {
    const filtered = AuditLogData.filter((log) => {
      const logDate = new Date(log.timestamp).toISOString().split("T")[0];
      const selectedDate = filterDate
        ? new Date(filterDate).toISOString().split("T")[0]
        : null;

      return (
        log.admin.toLowerCase().includes(searchAdmin.toLowerCase()) &&
        log.affectedUser
          .toLowerCase()
          .includes(searchAffectedUser.toLowerCase()) &&
        log.action.toLowerCase().includes(searchAction.toLowerCase()) &&
        (!selectedDate || logDate === selectedDate)
      );
    });
    setFilteredData(filtered);
  };

  const handleReset = () => {
    setSearchAdmin("");
    setSearchAffectedUser("");
    setSearchAction("");
    setFilterDate("");
    setFilteredData(AuditLogData);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Paper
      sx={{
        padding: { xs: 2, sm: 3, md: 5 },
        margin: "auto",
        position: "relative",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "16px", sm: "18px", lg: "18px", xl: "22px" },
            fontFamily: "Geist, sans-serif",
            fontWeight: 700,
            marginBottom: "20px",
          }}
        >
          Audit Log
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ flex: "1 1 200px" }}>
          <TextInput
            label="Full Name"
            placeholder="Enter Action"
            variant="outlined"
            size="small"
            fullWidth
            value={searchAction}
            onChange={(e) => setSearchAction(e.target.value)}
          />
        </Box>

        <Box sx={{ flex: "1 1 200px" }}>
          <TextInput
            label="Performed By"
            placeholder="Performed By"
            variant="outlined"
            size="small"
            fullWidth
            value={searchAdmin}
            onChange={(e) => setSearchAdmin(e.target.value)}
          />
        </Box>

        <Box sx={{ flex: "1 1 200px" }}>
          <TextInput
            label="Search Affected User"
            placeholder="Enter Affected User Name"
            variant="outlined"
            size="small"
            fullWidth
            value={searchAffectedUser}
            onChange={(e) => setSearchAffectedUser(e.target.value)}
          />
        </Box>

        <Box sx={{ flex: "1 1 200px" }}>
          <TextField
            label="Date"
            type="date"
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filterDate}
            onChange={handleDateChange}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flex: "1 1 200px", // make button group behave similarly
          }}
        >
          <CommonButton
            variant="outlined"
            startIcon={<Search />}
            onClick={handleSearch}
            sx={{
              textTransform: "capitalize",
              color: "white",
              background: "#006CB8",
              whiteSpace: "nowrap",
            }}
          >
            Search
          </CommonButton>
          <CommonButton
            variant="outlined"
            startIcon={<RestartAlt />}
            onClick={handleReset}
            sx={{ textTransform: "capitalize", whiteSpace: "nowrap" }}
          >
            Reset
          </CommonButton>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid transparent",
          borderRadius: "10px",
          maxWidth: "90vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
              <StyledTableCell>Performed By</StyledTableCell>
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell>IP</StyledTableCell>
              <StyledTableCell>Affected User/Item</StyledTableCell>
              <StyledTableCell sx={{ width: "150px", textAlign: "center" }}>
                Status
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData && filteredData?.length > 0 ? (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((log, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell>{log.action}</StyledTableCell>
                    <StyledTableCell>{log.admin}</StyledTableCell>
                    <StyledTableCell>
                      {new Date(log.timestamp).toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell>{log.ip_address}</StyledTableCell>
                    <StyledTableCell>{log.affectedUser}</StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "center" }}>
                      <Chip
                        label={log.status}
                        color={log.status === "Success" ? "success" : "error"}
                        variant="outlined"
                        sx={{
                          fontSize: { xs: "10px", sm: "12px" },
                          width: "80px",
                        }}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={9} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25,50,75,100]}
        sx={{
          fontSize: { xs: "12px", sm: "14px" },
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif",
          },
        }}
      />
    </Paper>
  );
};

export default AuditLog;
